import { UI_ELEMENTS_TO_HIDE } from '../constants/reportConstants';

const MAP_TILE_SELECTOR = '.leaflet-tile-pane img, .leaflet-overlay-pane img';

/**
 * Checks if element is a Leaflet map pane
 */
export function isMapPane(element: HTMLElement): boolean {
  const classList = element.classList;
  return (
    classList.contains('leaflet-map-pane') ||
    classList.contains('leaflet-tile-pane') ||
    classList.contains('leaflet-overlay-pane') ||
    classList.contains('leaflet-shadow-pane') ||
    classList.contains('leaflet-marker-pane') ||
    classList.contains('leaflet-tooltip-pane') ||
    classList.contains('leaflet-popup-pane')
  );
}

/**
 * Checks if element should be excluded from map capture
 */
export function shouldExcludeNode(element: Element): boolean {
  // Keep map panes
  if (element instanceof HTMLElement && isMapPane(element)) {
    return false;
  }

  // Check against exclusion selectors
  for (const selector of UI_ELEMENTS_TO_HIDE) {
    try {
      if (element.matches(selector)) {
        return true;
      }
    } catch (e) {
      // Invalid selector, skip
      continue;
    }
  }

  // Exclude position:fixed elements (app-level overlays like TemporalControl)
  if (element instanceof HTMLElement && window.getComputedStyle(element).position === 'fixed') {
    return true;
  }

  return false;
}

/**
 * Creates a filter function for html2canvas
 */
export function createMapFilter(): (node: Node) => boolean {
  return (node: Node): boolean => {
    if (!(node instanceof Element)) {
      return true; // Include text nodes, etc.
    }
    return !shouldExcludeNode(node);
  };
}

/**
 * Temporarily hides UI elements for map capture
 */
export function hideUIElements(): () => void {
  const hiddenElements: Array<{ element: HTMLElement; originalDisplay: string }> = [];

  const hideElement = (el: HTMLElement) => {
    if (!hiddenElements.some(h => h.element === el)) {
      hiddenElements.push({ element: el, originalDisplay: el.style.display });
      el.style.display = 'none';
    }
  };

  // 1. Hide by known selectors
  for (const selector of UI_ELEMENTS_TO_HIDE) {
    try {
      document.querySelectorAll<HTMLElement>(selector).forEach(hideElement);
    } catch (e) {
      // Invalid selector, skip
      continue;
    }
  }

  // 2. Hide any position:fixed element inside the leaflet container.
  //    This catches CSS-in-JS overlays (e.g. TemporalControl) that have no
  //    stable class/id but are positioned fixed relative to the viewport.
  const leafletContainer = document.querySelector('.leaflet-container');
  if (leafletContainer) {
    leafletContainer.querySelectorAll<HTMLElement>('*').forEach(el => {
      if (window.getComputedStyle(el).position === 'fixed') {
        hideElement(el);
      }
    });
  }

  // Return cleanup function
  return () => {
    hiddenElements.forEach(({ element, originalDisplay }) => {
      element.style.display = originalDisplay;
    });
  };
}

/**
 * Waits for map tiles to load (legacy timeout-based)
 */
export function waitForMapTiles(delay: number = 2000): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
}

/**
 * Waits for map tiles to actually load by checking tile image completion
 * More reliable than timeout-based approach
 */
export async function waitForMapTilesActualLoad(
  maxWaitMs: number = 3000
): Promise<boolean> {
  const startTime = Date.now();

  return new Promise((resolve) => {
    const checkInterval = setInterval(() => {
      // Find all tile images (base tiles + WMS overlays)
      const tileImages = document.querySelectorAll(
        MAP_TILE_SELECTOR
      ) as NodeListOf<HTMLImageElement>;

      if (tileImages.length === 0) {
        // No tiles found yet, keep waiting
        const elapsed = Date.now() - startTime;
        if (elapsed >= maxWaitMs) {
          clearInterval(checkInterval);
          console.warn(`[Map Capture] Timeout after ${elapsed}ms, no map images found`);
          resolve(false);
        }
        return;
      }

      // Check if all tiles are loaded
      const allLoaded = Array.from(tileImages).every(
        (img) => img.complete && img.naturalHeight > 0
      );

      const elapsed = Date.now() - startTime;

      if (allLoaded) {
        clearInterval(checkInterval);
        console.log(`[Map Capture] All ${tileImages.length} map images loaded after ${elapsed}ms`);
        resolve(true);
      } else if (elapsed >= maxWaitMs) {
        clearInterval(checkInterval);
        const loadedCount = Array.from(tileImages).filter(
          (img) => img.complete && img.naturalHeight > 0
        ).length;
        console.warn(
          `[Map Capture] Timeout after ${elapsed}ms, ${loadedCount}/${tileImages.length} tiles loaded`
        );
        resolve(false);
      }
    }, 100);
  });
}

/**
 * Gets the map container element
 */
export function getMapContainer(): HTMLElement | null {
  // Try common map container selectors
  const selectors = [
    '#map',
    '.leaflet-container',
    '[class*="map-container"]',
    '[id*="map"]',
  ];

  for (const selector of selectors) {
    const element = document.querySelector(selector);
    if (element instanceof HTMLElement) {
      return element;
    }
  }

  return null;
}

/**
 * Validates map container is ready for capture
 */
export function validateMapContainer(container: HTMLElement | null): void {
  if (!container) {
    throw new Error('Map container not found');
  }

  if (container.offsetWidth === 0 || container.offsetHeight === 0) {
    throw new Error('Map container has zero dimensions');
  }

  // Check if map has any visible content
  const hasVisibleContent = container.querySelector('.leaflet-tile-pane, .leaflet-overlay-pane');
  if (!hasVisibleContent) {
    throw new Error('Map has no visible content');
  }
}
